import { useMutation } from '@apollo/client';
import { EUserEditorAccessLevel } from '__generated__/globalTypes';
import { CheckValue } from 'elements/minute/sidebar/sidebarComponents/atomic/CheckValue';
import { Message } from 'elements/minute/sidebar/sidebarComponents/atomic/Message';
import { NumberValue } from 'elements/minute/sidebar/sidebarComponents/atomic/NumberValue';
import { SidebarContent } from 'elements/minute/sidebar/sidebarSection/SidebarContent';
import { SidebarSection } from 'elements/minute/sidebar/sidebarSection/SidebarSection';
import { useLocalConfig } from 'hooks/map/useLocalConfig';
import {
    UpdateUser,
    UpdateUserVariables
} from 'libs/graphql/mutation/user/__generated__/UpdateUser';
import { UPDATE_USER_MUTATION } from 'libs/graphql/mutation/user/userMutation';
import { useContext } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { GlobalContext } from 'session/state/context';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
}

export const ExperimentalSection = (props: Props): JSX.Element => {
    const globalContext = useContext(GlobalContext);
    const currentUser = globalContext.state.loggedUser;
    const localConfig = useLocalConfig();
    const ctxEdit = useContext(CtxEdit);

    const [update] = useMutation<UpdateUser, UpdateUserVariables>(UPDATE_USER_MUTATION, {});

    return (
        <S_ExperimentalSection className={props.className}>
            <SidebarSection title={Localization.EXPERIMENTAL} icon={{ iconName: 'flask' }}>
                <SidebarContent withHover={true} withTopPadding={true}>
                    <S_Message type="info">{Localization.EXPERIMENTAL_FEATURES_MESSAGE}</S_Message>

                    <S_Checkboxes>
                        <CheckValue
                            value={
                                currentUser?.userAccessLevel === EUserEditorAccessLevel.FULL
                                    ? true
                                    : false
                            }
                            label={
                                currentUser?.userAccessLevel === EUserEditorAccessLevel.FULL
                                    ? Localization.ENABLED
                                    : Localization.DISABLED
                            }
                            onChange={(value) => {
                                update({
                                    variables: {
                                        userInfo: {
                                            userAccessLevel: value
                                                ? EUserEditorAccessLevel.FULL
                                                : EUserEditorAccessLevel.FREE
                                        }
                                    }
                                });
                            }}
                        />

                        <CheckValue
                            label="Use shift to update polygons/walls"
                            value={localConfig.useShiftForPolygonUpdate}
                            onChange={(value) => localConfig.setUseShiftForPolygonUpdate(value)}
                        />

                        <S_NumberValue
                            label="Page of rendered markers (optimization)"
                            value={ctxEdit.markerRenderPage ?? 0}
                            onChange={(value) => {
                                if (value) {
                                    ctxEdit.setMarkerRenderPage(value);
                                } else {
                                    ctxEdit.setMarkerRenderPage(null);
                                }
                            }}
                        />
                    </S_Checkboxes>
                </SidebarContent>
            </SidebarSection>
        </S_ExperimentalSection>
    );
};

const S_ExperimentalSection = styled.div``;

const S_Message = styled(Message)`
    margin: 0 0 15px 0;
`;

const S_Checkboxes = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const S_NumberValue = styled(NumberValue)`
    width: 200px;
`;
